/**
 * MentionSuggestion - Tiptap mention suggestion renderer (shared/comments).
 * Dropdown of tenant users when typing '@' in the comment editor. Renders inline
 * with position:fixed to avoid portal/overlay stacking issues.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

export interface MentionSuggestionUser {
  id: string;
  full_name: string;
  email: string;
}

/**
 * Stable DOM id for the active suggestion row, so the editor's `.ProseMirror`
 * element (the combobox) can point `aria-activedescendant` at it.
 */
export const MENTION_OPTION_ID_PREFIX = 'mention-suggestion-option-';
export const mentionOptionId = (index: number) => `${MENTION_OPTION_ID_PREFIX}${index}`;
/** Stable id for the listbox container, referenced by the combobox `aria-controls`. */
export const MENTION_LISTBOX_ID = 'mention-suggestion-listbox';

interface MentionSuggestionListProps {
  items: MentionSuggestionUser[];
  command: (item: { id: string; label: string }) => void;
  /** Reports the active row's element id (or null) so the combobox can track it. */
  onActiveOptionChange?: (id: string | null) => void;
}

export const MentionSuggestionList = forwardRef<
  { onKeyDown: (props: { event: KeyboardEvent }) => boolean },
  MentionSuggestionListProps
>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const { onActiveOptionChange } = props;

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  // Keep the active row scrolled into view and report it to the combobox.
  useEffect(() => {
    if (props.items.length === 0) {
      onActiveOptionChange?.(null);
      return;
    }
    onActiveOptionChange?.(mentionOptionId(selectedIndex));
    const el = listRef.current?.querySelector<HTMLElement>(`[data-mention-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex, props.items, onActiveOptionChange]);

  useEffect(
    () => () => onActiveOptionChange?.(null),
    [onActiveOptionChange],
  );

  const selectItem = useCallback(
    (index: number) => {
      const item = props.items[index];
      if (item) {
        props.command({ id: item.id, label: item.full_name });
      }
    },
    [props.items, props.command],
  );

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((prev) => (prev + props.items.length - 1) % props.items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((prev) => (prev + 1) % props.items.length);
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (props.items.length === 0) {
    return null;
  }

  return (
    <div
      ref={listRef}
      id={MENTION_LISTBOX_ID}
      role="listbox"
      className="rounded-md border bg-popover p-1 shadow-md max-h-[200px] overflow-y-auto min-w-[200px]"
    >
      {props.items.map((item, index) => {
        const isActive = index === selectedIndex;
        return (
          <Button
            key={item.id}
            id={mentionOptionId(index)}
            data-mention-index={index}
            role="option"
            aria-selected={isActive}
            variant="ghost"
            size="sm"
            className={cn(
              'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm h-auto justify-start',
              isActive && 'bg-accent text-accent-foreground ring-2 ring-inset ring-primary',
            )}
            onClick={() => selectItem(index)}
          >
            <Avatar className="h-5 w-5">
              <AvatarFallback className="text-[10px]">{item.full_name.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col items-start min-w-0">
              <span className="truncate font-medium">{item.full_name}</span>
              <span className="truncate text-xs text-muted-foreground">{item.email}</span>
            </div>
          </Button>
        );
      })}
    </div>
  );
});

MentionSuggestionList.displayName = 'MentionSuggestionList';

/**
 * Inline fixed-position wrapper for the suggestion dropdown.
 * position:fixed + native mousedown preventDefault avoids portal stacking issues
 * and prevents editor blur when clicking the dropdown.
 */
interface MentionSuggestionDropdownProps {
  items: MentionSuggestionUser[];
  command: (item: { id: string; label: string }) => void;
  clientRect: (() => DOMRect | null) | null;
  listRef: React.Ref<{ onKeyDown: (props: { event: KeyboardEvent }) => boolean }>;
  /** Reports the active row's element id (or null) so the combobox can track it. */
  onActiveOptionChange?: (id: string | null) => void;
}

export function MentionSuggestionDropdown({
  items,
  command,
  clientRect,
  listRef,
  onActiveOptionChange,
}: MentionSuggestionDropdownProps) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!clientRect) {
      setPosition(null);
      return;
    }
    const rect = clientRect();
    if (!rect) {
      setPosition(null);
      return;
    }
    setPosition({ top: rect.bottom + 4, left: rect.left });
  }, [clientRect, items]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: MouseEvent) => {
      e.preventDefault();
    };
    el.addEventListener('mousedown', handler);
    return () => el.removeEventListener('mousedown', handler);
  }, [position]);

  if (!position || items.length === 0) return null;

  return (
    <div ref={containerRef} className="fixed z-50" style={{ top: position.top, left: position.left }}>
      <MentionSuggestionList
        ref={listRef}
        items={items}
        command={command}
        onActiveOptionChange={onActiveOptionChange}
      />
    </div>
  );
}
